import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ShieldCheck, BadgeCheck, Lock, Award } from "lucide-react";
import { COMPANY } from "@/lib/company";
import FAQ from "@/components/FAQ";

export default function Footer() {
  return (
    <footer className="bg-[#020617] border-t border-slate-800 mt-24" data-testid="global-footer">
      <div className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="mb-12">
            <span className="text-red-500 font-display text-xs tracking-[0.3em] font-bold">
              FREQUENTLY ASKED
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-3 tracking-tighter">
              Answers to the questions <br className="hidden md:block" />
              <span className="text-red-500">serious B2B buyers ask first.</span>
            </h2>
          </div>
          <FAQ />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-4 mb-5">
              <img
                src={COMPANY.logoUrl}
                onError={(e) => { e.currentTarget.src = COMPANY.logoFallback; }}
                alt="A.I.O"
                className="h-20 w-20 object-contain"
              />
              <div>
                <div className="font-display font-extrabold text-white text-lg leading-tight">
                  ALL IN ONE SOURCING
                </div>
                <div className="font-display text-red-500 text-xs tracking-[0.25em] font-semibold">
                  SPECIALISTS A.I.O
                </div>
              </div>
            </div>
            <p className="text-slate-400 max-w-md leading-relaxed">
              The ultimate one-stop sourcing hub for serious businesses. We locate, secure, negotiate, and deliver
              absolutely anything your operation requires &mdash; under one roof, under one vendor, under one invoice.
            </p>
          </div>

          <div>
            <h4 className="font-display font-bold text-white uppercase text-sm tracking-widest mb-5">
              Navigate
            </h4>
            <ul className="space-y-3 text-slate-400">
              <li><Link to="/" data-testid="footer-link-home" className="hover:text-red-500 transition-colors">Home</Link></li>
              <li><Link to="/activate" data-testid="footer-link-activate" className="hover:text-red-500 transition-colors">Activate Portal</Link></li>
              <li><Link to="/test-drive" data-testid="footer-link-testdrive" className="hover:text-red-500 transition-colors">Test Drive</Link></li>
              <li><Link to="/contact" data-testid="footer-link-contact" className="hover:text-red-500 transition-colors">Request Quote</Link></li>
              <li><Link to="/terms" data-testid="footer-link-terms" className="hover:text-red-500 transition-colors">Terms of Use</Link></li>
              <li><Link to="/privacy" data-testid="footer-link-privacy" className="hover:text-red-500 transition-colors">Privacy Policy</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display font-bold text-white uppercase text-sm tracking-widest mb-5">
              Direct Line
            </h4>
            <ul className="space-y-4 text-slate-300">
              <li>
                <a href={COMPANY.phoneLink} data-testid="footer-phone" className="flex items-start gap-3 hover:text-red-500 transition-colors">
                  <Phone className="w-4 h-4 mt-1 text-red-500" />
                  <span>{COMPANY.phone}</span>
                </a>
              </li>
              <li>
                <a href={COMPANY.emailLink} data-testid="footer-email" className="flex items-start gap-3 hover:text-red-500 transition-colors break-all">
                  <Mail className="w-4 h-4 mt-1 text-red-500 shrink-0" />
                  <span>{COMPANY.email}</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-slate-400">
                <MapPin className="w-4 h-4 mt-1 text-red-500 shrink-0" />
                <span>United States <br />Nationwide B2B Sourcing</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Compact trust strip */}
        <div
          className="mt-10 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 py-3 px-4 border-y border-slate-800/80 bg-[#0B1221]/40"
          data-testid="footer-trust-strip"
        >
          <span className="flex items-center gap-1.5 text-slate-300 font-display text-[11px] tracking-widest uppercase">
            <ShieldCheck className="w-3.5 h-3.5 text-red-500" /> Fully Insured
          </span>
          <span className="text-slate-700">|</span>
          <span className="flex items-center gap-1.5 text-slate-300 font-display text-[11px] tracking-widest uppercase">
            <BadgeCheck className="w-3.5 h-3.5 text-red-500" /> Verified B2B
          </span>
          <span className="text-slate-700">|</span>
          <span className="flex items-center gap-1.5 text-slate-300 font-display text-[11px] tracking-widest uppercase">
            <Lock className="w-3.5 h-3.5 text-red-500" /> Stripe Secured
          </span>
          <span className="text-slate-700">|</span>
          <span className="flex items-center gap-1.5 text-slate-300 font-display text-[11px] tracking-widest uppercase">
            <Award className="w-3.5 h-3.5 text-red-500" /> Corporate Elegance
          </span>
        </div>

        <div className="mt-6 pt-6 border-t border-slate-800" data-testid="compliance-disclosure">
          <p className="text-slate-500 text-[11px] leading-relaxed">
            <span className="font-display font-bold text-slate-400 tracking-wider uppercase">Legal, Compliance, &amp; Industry Disclosures:</span>{" "}
            All In One Sourcing Specialists A.I.O is a registered trade name and authorized DBA of <span className="text-slate-400">DGKS Sourcing Specialists LLC</span>, an independent corporate procurement and supply chain sourcing consultancy operating under verified North Carolina limited liability structures. Promotional travel certificates distributed via our platform are handled by a premium third-party affiliate travel provider. Vouchers cover standard baseline resort room rates completely; all local municipal hotel taxes, tourism assessments, and mandatory resort fees ranging from <span className="text-slate-400">$34 to $89 per night</span> are the exclusive financial responsibility of the end-user traveler at booking. DGKS Sourcing Specialists LLC, doing business as All In One Sourcing Specialists A.I.O, does not operate, manage, or hold physical room inventory for the participating properties and is held entirely harmless, immune, and free from any liability involving room stays, travel issues, processing friction, injuries, or scheduling changes executed by the third-party travel fulfillment platform. All disputes are governed under the exclusive jurisdiction of the state courts in <span className="text-slate-400">Guilford County, North Carolina</span>.
          </p>
        </div>

        <div className="mt-6 pt-6 border-t border-slate-800 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <p className="text-slate-500 text-sm">
            &copy; {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
          </p>
          <p className="text-slate-600 text-xs font-display tracking-widest uppercase" data-testid="footer-tagline">
            Aggressive Global Sourcing &middot; One Vendor &middot; One Invoice
          </p>
        </div>
      </div>
    </footer>
  );
}
